import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { CompontHeader } from './compont-header';
import { LinkPreview } from './link-preview';
import { TESTIMONIAL_DATA, TestimonialType } from '../data/review';

const CARD_SIZE_LG = 300;
const CARD_SIZE_SM = 300;

const BORDER_SIZE = 2;
const CORNER_CLIP = 50;
const CORNER_LINE_LEN = Math.sqrt(
  CORNER_CLIP * CORNER_CLIP + CORNER_CLIP * CORNER_CLIP,
);

const ROTATE_DEG = 2.5;

const STAGGER = 15;
const CENTER_STAGGER = -65;

const SECTION_HEIGHT = 600;

export const Reviews = () => {
  const [cardSize, setCardSize] = useState(CARD_SIZE_LG);

  const [testimonials, setTestimonials] = useState(TESTIMONIAL_DATA);

  const handleMove = (position: number) => {
    const copy = [...testimonials];

    if (position > 0) {
      for (let i = position; i > 0; i--) {
        const firstEl = copy.shift();

        if (!firstEl) return;

        copy.push({ ...firstEl, tempId: Math.random() });
      }
    } else {
      for (let i = position; i < 0; i++) {
        const lastEl = copy.pop();

        if (!lastEl) return;

        copy.unshift({ ...lastEl, tempId: Math.random() });
      }
    }

    setTestimonials(copy);
  };

  useEffect(() => {
    const { matches } = window.matchMedia('(min-width: 640px)');

    if (matches) {
      setCardSize(CARD_SIZE_LG);
    } else {
      setCardSize(CARD_SIZE_SM);
    }

    const handleSetCardSize = () => {
      const { matches } = window.matchMedia('(min-width: 640px)');

      if (matches) {
        setCardSize(CARD_SIZE_LG);
      } else {
        setCardSize(CARD_SIZE_SM);
      }
    };

    window.addEventListener('resize', handleSetCardSize);

    return () => window.removeEventListener('resize', handleSetCardSize);
  }, []);

  return (
    <div id="reviews">
      <div className="pt-[84px] pb-6">
        <CompontHeader text="Kundenrezensionen" />
      </div>
      <div className="text-center text-3xl pb-3">
        Was unsere Kunden sagen!{' '}
        <div className="text-nowrap">
          ({' '}
          <LinkPreview
            url="https://www.google.com/search?sa=X&sca_esv=963e6bd44828a6b7&rlz=1C5CHFA_enDE1086DE1086&biw=1338&bih=708&tbm=lcl&sxsrf=ADLYWIKY4bn8o_fiyQNfaoJxfROiFkM2Kw:1734597883539&q=rezensionen%20f%C3%BCr%20schmeckt-lecker&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2N7U0NjYwtTA1MrI0MzAzNzK22MDI-IpRoSi1KjWvODM_LzVPIe3wniKF4uSM3NTk7BLdHCCZWrSIlaASAI18YCdgAAAA&rldimm=7593305852296067238&hl=de-DE&ved=0CAgQ5foLahcKEwiAzdvVuLOKAxUAAAAAHQAAAAAQBw#lkt=LocalPoiReviews&arid=ChZDSUhNMG9nS0VJQ0FnSUN2XzZ1U1l3EAE"
            className="font-bold"
          >
            Google
          </LinkPreview>{' '}
          )
        </div>
      </div>

      <div className="relative w-full overflow-hidden sm:h-[calc(100vh - 200px)] h-[calc(100vh-240px)]">
        {testimonials.map((t, idx) => {
          let position = 0;

          if (testimonials.length % 2) {
            position = idx - (testimonials.length + 1) / 2;
          } else {
            position = idx - testimonials.length / 2;
          }

          return (
            <TestimonialCard
              key={t.tempId}
              testimonial={t}
              handleMove={handleMove}
              position={position}
              cardSize={cardSize}
            />
          );
        })}
        <div className="absolute bottom-4 left-1/2 flex flex-col -translate-x-1/2 gap-4 w-full px-2">
          <div className="flex justify-center">
            <button
              onClick={() => handleMove(-1)}
              className="grid h-14 w-14 place-content-center text-3xl transition-colors  hover:text-white"
            >
              <GoArrowLeft />
            </button>
            <button
              onClick={() => handleMove(1)}
              className="grid h-14 w-14 place-content-center text-3xl transition-colors  hover:text-white"
            >
              <GoArrowRight />
            </button>
          </div>
          <div className="text-xs text-center">
            Diese Bewertungen wurden von Google Reviews übernommen. Die
            vollständigen Bewertungen können Sie auf unserem{' '}
            <LinkPreview
              url="https://www.google.com/search?sa=X&sca_esv=963e6bd44828a6b7&rlz=1C5CHFA_enDE1086DE1086&biw=1338&bih=708&tbm=lcl&sxsrf=ADLYWIKY4bn8o_fiyQNfaoJxfROiFkM2Kw:1734597883539&q=rezensionen%20f%C3%BCr%20schmeckt-lecker&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2N7U0NjYwtTA1MrI0MzAzNzK22MDI-IpRoSi1KjWvODM_LzVPIe3wniKF4uSM3NTk7BLdHCCZWrSIlaASAI18YCdgAAAA&rldimm=7593305852296067238&hl=de-DE&ved=0CAgQ5foLahcKEwiAzdvVuLOKAxUAAAAAHQAAAAAQBw#lkt=LocalPoiReviews&arid=ChZDSUhNMG9nS0VJQ0FnSUN2XzZ1U1l3EAE"
              className="hover:underline"
            >
              Google Business Profil
            </LinkPreview>{' '}
            einsehen.
          </div>
        </div>
      </div>
    </div>
  );
};

interface TestimonialProps {
  position: number;
  testimonial: TestimonialType;
  handleMove: Function;
  cardSize: number;
}

const TestimonialCard = ({
  position,
  testimonial,
  handleMove,
  cardSize,
}: TestimonialProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isActive = position === 0;

  const renderStars = (stars: number) => {
    const fullStars = Array(stars).fill(true);
    const emptyStars = Array(5 - stars).fill(false);

    return [...fullStars, ...emptyStars].map((isFull, index) => (
      <svg
        key={index}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill={isFull ? 'orange' : 'gray'}
        className="w-5 h-5"
      >
        <path
          fillRule="evenodd"
          d="M10 15.27l4.18 2.18-1.64-5.27L18 7.73l-5.36-.42L10 2 7.36 7.31 2 7.73l4.46 4.45-1.64 5.27L10 15.27z"
          clipRule="evenodd"
        />
      </svg>
    ));
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (!isActive) {
      handleMove(position);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isActive) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <motion.div
      initial={false}
      onClick={handleCardClick}
      className={`
          absolute left-1/2 top-1/2 cursor-pointer border-black p-8 text-black transition-colors duration-500
          ${isActive ? 'z-10 bg-[#FAED04]' : 'z-0 bg-white'}
        `}
      style={{
        borderWidth: BORDER_SIZE,
        clipPath: `polygon(${CORNER_CLIP}px 0%, calc(100% - ${CORNER_CLIP}px) 0%, 100% ${CORNER_CLIP}px, 100% 100%, calc(100% - ${CORNER_CLIP}px) 100%, ${CORNER_CLIP}px 100%, 0 100%, 0 0)`,
      }}
      animate={{
        width: cardSize,
        height: isExpanded ? cardSize * 1.5 : cardSize,
        x: `calc(-50% + ${position * (cardSize / 1.5)}px)`,
        y: `calc(-50% + ${
          isActive ? CENTER_STAGGER : position % 2 ? STAGGER : -STAGGER
        }px)`,
        rotate: isActive ? 0 : position % 2 ? ROTATE_DEG : -ROTATE_DEG,
        boxShadow: isActive ? '0px 8px 0px 4px black' : '0px 0px 0px 0px black',
      }}
      transition={{
        type: 'spring',
        mass: 3,
        stiffness: 400,
        damping: 50,
      }}
    >
      <span
        className="absolute block origin-top-right rotate-45 bg-black object-cover"
        style={{
          right: -BORDER_SIZE,
          top: CORNER_CLIP - BORDER_SIZE,
          width: CORNER_LINE_LEN,
          height: BORDER_SIZE,
        }}
      />
      {!isExpanded && (
        <div className="flex space-x-1 mb-2">
          {renderStars(testimonial.stars)}
        </div>
      )}
      <div className="relative">
        <h3
          className={`text-base sm:text-xl text-black ${
            isExpanded ? '' : 'line-clamp-5'
          } overflow-hidden`}
        >
          "{testimonial.testimonial}"
        </h3>
        {isActive && testimonial.testimonial.length > 200 && (
          <button
            onClick={handleExpandClick}
            className="mt-2 text-sm font-medium text-indigo-700 hover:text-indigo-900"
          >
            {isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
          </button>
        )}
      </div>
      {!isExpanded && (
        <div
          className={`absolute flex justify-between bottom-8 left-8 right-8 mt-2 text-sm italic ${
            isActive ? 'text-indigo-700' : 'text-neutral-700'
          }`}
        >
          <p>- {testimonial.by}</p>
          <p>{testimonial.date}</p>
        </div>
      )}
    </motion.div>
  );
};
